import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { X, FileDown, Check } from "lucide-react";

import { Worker } from "../../types/workpermit";
import { GetWorkPermit } from "../../services/workPermitService";
import { resolveImageUrl } from "../../utils/imageHelper";

export default function QRCodeModal({
  isOpen,
  onClose,
  permitId,
}: {
  isOpen: boolean;
  onClose: () => void;
  permitId: number;
}) {
  const [activeTab, setActiveTab] = useState<"qrcode" | "ktp">("qrcode");
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string>("");
  const [, setIsLoading] = useState(false);
  const [permitStatusDesc, setPermitStatusDesc] = useState<string>("");

  const fetchDetailWorkers = async (workPermitId: number) => {
    if (!workPermitId) return;

    setIsLoading(true);
    try {
      const response = await GetWorkPermit(workPermitId, 0, 0, 0, 0, 0);

      if (response.workPermitHeaderDatas && response.workPermitHeaderDatas.length > 0) {
        const header = response.workPermitHeaderDatas[0];
        
        const mappedWorkers: Worker[] = (header.personnelDatas || []).map((p, idx) => ({
          WorkPermitPersonnelId: idx + 1,
          PersonnelName: p.PersonnelName || "",
          IdentityNumber: p.IdentityNumber || "",
          PersonnelQRCode: p.PersonnelQRCode || "",
          FileImage: p.FileImage || "",
          DataImage: "",
          ImageLabel: p.ImageLabel || "Foto KTP",
          IsAccessCard: p.IsAccessCard || 0,
          IsParkingLotKey: p.IsParkingLotKey || 0,
        }));
        setWorkers(mappedWorkers);

        let statusDesc = "Pending Approval";
        if (header.workPermitDatas && header.workPermitDatas.length > 0) {
          const matchingPermit = header.workPermitDatas.find(p => p.WorkPermitId === workPermitId) || header.workPermitDatas[0];
          const isApproved = matchingPermit.PermitStatus === 20 ||
                             header.workPermitDatas.every(p => p.PermitStatus === 20) ||
                             matchingPermit.PermitStatusDesc?.toLowerCase() === "approved";
          statusDesc = isApproved ? "Approved" : (matchingPermit.PermitStatusDesc || "Pending Approval");
        } else {
          const isApproved = header.PermitHeaderStatus === 20 || header.PermitHeaderStatusDesc?.toLowerCase() === "approved";
          statusDesc = isApproved ? "Approved" : (header.PermitHeaderStatusDesc || "Pending Approval");
        }
        setPermitStatusDesc(statusDesc);
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
      setWorkers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && permitId) {
      fetchDetailWorkers(permitId);
    }
  }, [isOpen, permitId]);

  const handleSelectWorker = (id: string) => {
    setSelectedWorkerId(id);
    setWorkers(
      workers.map((w) => ({
        ...w,
        selected: w.PersonnelQRCode === id,
      }))
    );
  };
  const selectedWorker = workers.find(
    (w) => w.PersonnelQRCode === selectedWorkerId
  );
  const handleDownloadQR = () => {
    const svg = document.getElementById("qr-code-svg");
    if (svg) {
      const svgData = new XMLSerializer().serializeToString(svg);
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `QRCode-Permit-${permitId}-Worker-${selectedWorkerId}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      img.src =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(svgData)));
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">

      <div className="absolute inset-0 bg-black/40" onClick={onClose} />


      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl z-10">

        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-gray-400 hover:text-gray-600 transition z-10"
        >
          <X className="w-5 h-5" />
        </button>


        {permitStatusDesc !== "Approved" ? (
          <div className="flex flex-col items-center justify-center py-10">
            <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mb-4">
              <span className="text-yellow-600 text-3xl">⏳</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Permit belum disetujui
            </h3>
            <p className="text-xs text-gray-500 text-center max-w-xs">
              QR Code hanya bisa dilihat setelah status permit <b>Approved</b>.
              Silakan tunggu approval.
            </p>
          </div>
        ) : (
          <>
            <div className="p-5 pt-6">

              <h2 className="text-base font-bold text-center text-gray-900 mb-4">
                QR Code Access
              </h2>

              <div className="flex justify-center mb-3">
                {activeTab === "qrcode" ? (
                  selectedWorkerId ? (
                    <QRCodeSVG
                      id="qr-code-svg"
                      value={`${selectedWorkerId}`}
                      size={180}
                      level="H"
                      includeMargin={false}
                    />
                  ) : (
                    <div className="w-[180px] h-[180px] flex items-center justify-center bg-gray-100 rounded-lg">
                      <span className="text-gray-400 text-sm text-center px-4">
                        Pilih pekerja untuk melihat QR Code
                      </span>
                    </div>
                  )
                ) : (
                  <div className="w-[280px] h-[180px] flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
                    {selectedWorker?.FileImage ? (
                      <img
                        src={resolveImageUrl(selectedWorker.FileImage)}
                        alt={`KTP ${selectedWorker.PersonnelName}`}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.src = "";
                          e.currentTarget.alt = "Gambar tidak tersedia";
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">
                        Pilih pekerja untuk melihat KTP
                      </span>
                    )}
                  </div>
                )}
              </div>


              <div className="flex justify-center mb-4">
                <button
                  onClick={handleDownloadQR}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white rounded-full text-xs font-medium hover:bg-gray-800 transition"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  Download QR Code
                </button>
              </div>


              <div className="flex justify-center gap-2 mb-4">
                <button
                  onClick={() => setActiveTab("qrcode")}
                  className={`px-5 py-1.5 rounded-full text-xs font-medium transition ${activeTab === "qrcode"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                >
                  QR Code
                </button>
                <button
                  onClick={() => setActiveTab("ktp")}
                  className={`px-5 py-1.5 rounded-full text-xs font-medium transition ${activeTab === "ktp"
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                >
                  Lihat KTP
                </button>
              </div>


              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">
                  List Pekerja
                </h3>


                <div className="space-y-2 max-h-40 overflow-y-auto pt-2">
                  {workers.map((worker) => (
                    <div
                      key={worker.WorkPermitPersonnelId}
                      onClick={() => handleSelectWorker(worker.PersonnelQRCode)}
                      className={`p-2.5 rounded-xl border cursor-pointer transition ${worker.PersonnelQRCode === selectedWorkerId
                        ? "border-blue-500 bg-blue-50/50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                    >
                      <div className="flex items-center justify-between">

                        <div className="flex-1 min-w-0 pr-3">
                          <p className="text-sm font-medium text-gray-900">
                            {worker.PersonnelName}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {worker.IdentityNumber}
                          </p>
                        </div>


                        <div className="text-center px-2">
                          <p className="text-xs text-gray-500 mb-0.5">
                            Perlu Kartu Akses
                          </p>
                          <p className="text-xs font-medium text-gray-900">
                            {worker.IsAccessCard === 1 ? "Ya" : "Tidak"}
                          </p>
                        </div>


                        <div className="text-center px-2">
                          <p className="text-xs text-gray-500 mb-0.5">
                            Perlu Kunci Lot
                          </p>
                          <p className="text-xs font-medium text-gray-900">
                            {worker.IsParkingLotKey === 1 ? "Ya" : "Tidak"}
                          </p>
                        </div>


                        <div className="flex-shrink-0 ml-2">
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${worker.PersonnelQRCode === selectedWorkerId
                              ? "border-blue-600 bg-blue-600"
                              : "border-gray-300 bg-white"
                              }`}
                          >
                            {worker.PersonnelQRCode === selectedWorkerId && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
